<template>
    <div :id="group + '_' + name + '_label'"
        class="tab-bar-label"
        :class="[{'is-active': active, 'is-valid': valid, 'is-invalid': invalid, 'step-label': useStep, disabled, 'show-tooltip': disabled}, 'tab-bar-label-' + group]"
        v-interact="() => changeTab(true)"
        type="button"
        role="tab"
        :aria-selected="active ? 'true' : 'false'"
        :aria-controls="group + '_' + name">
        <span v-if="useStep" class="step-label-indicator">
            <i v-if="validated" class="material-icons md-18" aria-hidden="true" v-text="isValid ? 'check' : 'close'" role="img"></i>
            <span class="step-number">{{useStep}}.</span>
        </span>
        <slot :is-valid="isValid" :validated="validated" :title="title">
            <span class="tab-bar-label-text">{{title}}</span>
        </slot>
    </div>
</template>

<script lang="ts">
    import {getVueParent} from '@/library/dom';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {applyChangesPrompt, getVueChildren, NoOperation} from '@/library/utils';
    import {type ComponentPublicInstance} from 'vue';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {type RouteLocationRaw} from 'vue-router';
    import LocalStorage from '../library/localStorage';
    import FormSteps, {type FormStepIntention} from './form/FormSteps.vue';

    interface FormStepFormComponent extends ComponentPublicInstance
    {
        save: (formSteps: FormSteps, intention: FormStepIntention) => Promise<any>;
    }

    @Component({
        name: 'TabBarLabel',
        emits: ['activated', 'save'],
    })
    export class TabBarLabel extends Vue
    {
        @Prop({
            type: String,
            required: true,
        }) name: string;

        @Prop({
            type: String,
            required: true,
        }) group: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) default: boolean;

        @Prop({
            type: String,
            default: '',
            required: false,
        }) title: string;

        @Prop({
            type: [Number, Boolean],
            default: false,
            required: false,
        }) useStep: number | false;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) forceShow: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) preventSubmitOnBack: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) loading: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) disabled: boolean;

        validated = false;

        isValid: number = 0; // 0 = invalid, 1 = valid

        formSteps: Nullable<FormSteps> = null;

        get active(): boolean
        {
            return this.forceShow || (this.options && this.options.active === this.name);
        }

        get options(): TabBarData
        {
            return this.$store.state.tabBar[this.group];
        }

        get localStorageKey(): string
        {
            return 'tab-bar-active.' + this.group;
        }

        get valid()
        {
            return this.validated && this.isValid;
        }

        get invalid()
        {
            return this.validated && !this.isValid;
        }

        get useStorage()
        {
            return this.options?.store && 'TabBarLabels' === this.$parent?.$options.name && this.$parent.$store;
        }

        @Watch('$route.params', {deep: true})
        watchParams()
        {
            if (this.options.paramName && this.$route.params[this.options.paramName] && this.$route.params[this.options.paramName] !== this.options.active)
            {
                this.update({active: this.$route.params[this.options.paramName] as string});
            }
        }

        @Watch('group')
        watchGroup()
        {
            this.init();
        }

        created()
        {
            this.init();

            this.emitter.on('form-step:validated', (state: number) =>
            {
                this.validated = true;
                this.isValid   = state;
            });
        }

        mounted()
        {
            // activate first tab if no active tab given
            if (this.options && !this.options.active)
            {
                this.update({active: this.options.first});
            }

            let formSteps = getVueParent(this, 'FormSteps') as Nullable<FormSteps>;

            // be sure $parent is FormSteps, to active some other features [e.g. step validation]
            if (formSteps)
            {
                this.formSteps = formSteps;
            }
        }

        init()
        {
            if (!this.options)
            {
                return;
            }

            // set first tab
            if (!this.options.first)
            {
                this.update({first: this.name});
            }

            if (this.options.paramName && this.$route.params[this.options.paramName])
            {
                this.update({active: this.$route.params[this.options.paramName]});
                this.$emit('activated', this.$route.params[this.options.paramName]);
                this.emitter.emit('activated', this.$route.params[this.options.paramName]);
            }

            // activate stored tab
            const savedTab = LocalStorage.get(this.localStorageKey, null);

            if (this.useStorage && !this.options.active && savedTab)
            {
                this.update({active: savedTab});
                this.$emit('activated', savedTab);
                this.emitter.emit('activated', savedTab);
            }

            // activate default tab
            if (!this.options.active && this.default)
            {
                this.update({active: this.name});
                this.$emit('activated', this.name);
                this.emitter.emit('activated', this.name);
            }
        }

        changeTab(labelClicked = false): void
        {
            // prevent errors caused by switching between tabs too quickly
            if (this.loading || this.disabled)
            {
                return;
            }

            // if form steps is enabled then check async if tab is valid
            const formSteps = this.formSteps;

            if (!formSteps || !formSteps.labels)
            {
                this.activateTab();

                return;
            }

            let index = 0;

            for (let i = 0; i < formSteps.labels.length; i++)
            {
                if (formSteps.getElements(i).label === this)
                {
                    index = i;
                    break;
                }
            }

            if (
                // boundaries
                formSteps.currentMaxStep + 1 < index
                // tab didn't change
                || labelClicked && index === formSteps.current
            )
            {
                return;
            }

            formSteps.intention = index < formSteps.current ? 'previous' : 'next';

            if (formSteps.intention === 'previous')
            {
                if (!formSteps.stepAsComponent || formSteps.getElements(formSteps.current).label?.preventSubmitOnBack)
                {
                    formSteps!.setStep(index, true);

                    return;
                }

                applyChangesPrompt()
                .then(() => this.validateAndSetStep(index))
                .catch(() => formSteps!.setStep(index, true));

                return;
            }
            else if (labelClicked && formSteps.currentMaxStep < index)
            {
                // clicked on currentMaxStep + 1 -> trigger next
                if (formSteps.current === formSteps.currentMaxStep && index === formSteps.currentMaxStep + 1)
                {
                    formSteps.next();

                    return;
                }

                // one of the previous steps is invalid or hasn't been validated yet -> prevent
                for (let i = 0; i <= formSteps.currentMaxStep; i++)
                {
                    if (!formSteps.getElements(i).label?.isValid)
                    {
                        return;
                    }
                }
            }

            this.validateAndSetStep(index);

            return;
        }

        validateAndSetStep(index: number)
        {
            return new Promise((resolve, reject) =>
            {
                const formSteps = this.formSteps;

                formSteps?.validateCurrent().then(() =>
                {
                    if (!formSteps.stepAsComponent)
                    {
                        formSteps.setStep(index, true);
                        return;
                    }

                    let currentContent = formSteps.getElements(formSteps.current).content;

                    if (!currentContent)
                    {
                        throw new Error('no matching tab-bar-content found');
                    }

                    const tabBarContentChildren = getVueChildren(currentContent.$el);

                    if (!tabBarContentChildren[0])
                    {
                        throw new Error('no form component even though the option stepAsComponent is in use');
                    }

                    const formComponent = tabBarContentChildren[0] as FormStepFormComponent;

                    if (!formComponent.save || typeof formComponent.save !== 'function')
                    {
                        throw new Error('form component does not own a save method');
                    }

                    const onSaveResolved = (data: any) =>
                    {
                        formComponent.$emit('save', this.formSteps?.intention);
                        formComponent.emitter.emit('save', this.formSteps?.intention);

                        formSteps.setStep(index, true);
                        resolve(data);
                    };

                    // support is able to access all steps
                    // skip before validation because generally in earlier steps, entries required for further context would be created
                    if (this.currentUser.isSupport)
                    {
                        TopMessageHandler.info('Es wurden keine Daten gespeichert, da Sie als Support-Nutzer angemeldet sind.');

                        onSaveResolved(null);
                        return;
                    }

                    formComponent.save(formSteps, this.formSteps?.intention || null)
                    .then(onSaveResolved)
                    .catch(error =>
                    {
                        console.error(error);
                        reject(error);
                    });
                })
                .catch(error =>
                {
                    console.error('form invalid!', error);
                    reject(error);
                });
            });
        }

        activateTab(): void
        {
            this.update({active: this.name});
            this.$emit('activated', this.name);
            this.emitter.emit('activated', this.name);

            if (this.options.paramName)
            {
                let route: RouteLocationRaw = {
                    name: <any>this.$route.name,
                    hash: this.$route.hash,
                    query: this.$route.query,
                    params: this.$route.params,
                    force: true,
                };

                if (route.params)
                {
                    if (route.params[this.options.paramName] === this.name)
                    {
                        return;
                    }

                    route.params[this.options.paramName] = this.name;
                }

                this.$router.replace(route).catch(NoOperation);
            }

            if (this.useStorage)
            {
                LocalStorage.set(this.localStorageKey, this.name);
            }
        }

        update(data: Partial<TabBarData>): void
        {
            this.$store.commit(
                'updateTabBar',
                {
                    group: this.group,
                    data: Object.assign({}, this.options, data),
                },
            );
        }
    }

    export default TabBarLabel;
</script>